import { useCallback, useEffect, useRef, useState } from "react";
import { IconButton, Stack, Typography, useTheme } from "@mui/material";
import OtpInput from 'react-otp-input';
import { ButtonComponent } from "../../components/form";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { decrypt, encrypt } from '../../utils/customFunctions';
import { useRequestOtpMutation, useVerifyOtpMutation } from "../../services/authApi";
import SnackbarComponent from "../../components/snackBar/snackbarComponent";
import { GetAuth } from "../../context/auth/authProvider";
import { useNavigate } from "react-router-dom";

const OtpView = ({
    back
}) => {
    const theme = useTheme();

    const [otp, setOtp] = useState('');

    const [otpValid, setOtpValid] = useState(true);
    const [otpValidTime, setOtpValidTime] = useState(30);

    const [mobileNumber, setMobileNumber] = useState('');

    const [otpError, setOtpError] = useState(null);

    const [snackBar, setSnackBar] = useState({
        message: "",
        open: false,
        color: ""
    });

    const navigate = useNavigate();

    const auth = GetAuth();

    const otpSuccessHandledRef = useRef(false);

    useEffect(() => {
        const encryptedAuthData = sessionStorage.getItem('authSession');

        if(encryptedAuthData){
            const decryptedAuthData = decrypt(encryptedAuthData);
            const authObj = JSON.parse(decryptedAuthData);

            setMobileNumber(authObj.mobile);

            const elapsedSeconds = (new Date().getTime() - authObj.time) / 1000;

            if(elapsedSeconds < 30){
                setOtpValidTime(30 - elapsedSeconds.toFixed(0));
                setOtpValid(true);
            }
            else{
                setOtpValid(false);
            }
        }
    }, []);

    useEffect(() => {
        if(otpValid){
            if (otpValidTime === 0) return;

            const timer = setInterval(() => {
                setOtpValidTime(value => value - 1);
            }, 1000);

            return () => clearInterval(timer);
        }
    }, [otpValid]);

    useEffect(() => {
        if(otpValidTime === 0){
            setOtpValid(false);
        }
    }, [otpValidTime]);

    const [
        requestOtp, 
        {
            isLoading: isLoadingRequestOtp,
            isSuccess: isSuccessRequestOtp,
            isError: isErrorRequestOtp,
            error: errorRequestOtp
        }
    ] = useRequestOtpMutation();
    
    useEffect(() => {
        if(isSuccessRequestOtp){
            setSnackBar({
                message: "We've sent a new otp to your mobile",
                color: "green",
                open: true
            });
    
            const authObj = {
                'mobile': mobileNumber,
                'time': new Date().getTime()
            }
            
            const encryptedData = encrypt(authObj);
            sessionStorage.setItem('authSession', encryptedData);

            setOtpValidTime(30); 
            setOtpValid(true);
        }
    }, [isSuccessRequestOtp]);
    
    useEffect(() => {
        if(isErrorRequestOtp && errorRequestOtp){
            const message =
                errorRequestOtp?.data?.message ||
                errorRequestOtp?.data?.error?.message ||
                errorRequestOtp?.error ||
                'Failed to send OTP';

            setSnackBar({
                message: message,
                color: "red",
                open: true
            });
        }
    }, [isErrorRequestOtp, errorRequestOtp]);

    const requestOtpAgain = useCallback(() => {
        requestOtp({mobileNo: mobileNumber});
    }, [mobileNumber, requestOtp]);

    const [
        verifyUserOtp, 
        {
            isLoading: isLoadingVerifyUserOtp,
            isSuccess: isSuccessVerifyUserOtp,
            data: dataVerifyUserOtp,
            isError: isErrorVerifyUserOtp,
            error: errorVerifyUserOtp
        }
    ] = useVerifyOtpMutation();

    useEffect(() => {
        if(isSuccessVerifyUserOtp && dataVerifyUserOtp && !otpSuccessHandledRef.current){
            otpSuccessHandledRef.current = true;
            console.log(otp, mobileNumber);
            console.log("OTP verification response:", dataVerifyUserOtp);
            if(dataVerifyUserOtp.success || dataVerifyUserOtp.customToken){
                console.log("OTP verification successful, proceeding with login...");
                setSnackBar({
                    message: "Login success",
                    color: "green",
                    open: true
                });
        
                sessionStorage.removeItem('authSession');
                console.log("Removed authSession from sessionStorage");

                (async () => {
                    await auth.saveLoginResponse(dataVerifyUserOtp, mobileNumber);
                    console.log("Login response saved, navigating to records...");
                    setTimeout(() => {
                        navigate('/records');
                    }, 2000);
                })();
            }
            else{
                setSnackBar({
                    message: dataVerifyUserOtp.message,
                    color: "red",
                    open: true
                });
            }
        }
    }, [isSuccessVerifyUserOtp, dataVerifyUserOtp]);
    
    useEffect(() => {
        if(isErrorVerifyUserOtp && errorVerifyUserOtp){
            const message =
                errorVerifyUserOtp?.data?.message ||
                errorVerifyUserOtp?.data?.error?.message ||
                errorVerifyUserOtp?.error ||
                'Verification failed';

            setSnackBar({
                message: message,
                color: "red",
                open: true
            });
        }
    }, [isErrorVerifyUserOtp, errorVerifyUserOtp]);

    const verifyOtp = useCallback(() => {
        if(otp.length !== 6){
            setOtpError('Invalid otp');
            return;
        }

        setOtpError(null);
        console.log("Verifying OTP for mobile:", mobileNumber, "with OTP:", otp);
        verifyUserOtp({mobileNo: mobileNumber, otp: otp});
    }, [otp, mobileNumber, verifyUserOtp]);

    return (
        <>
            <Stack
                direction='column'
                justifyContent='center'
                alignItems='center'
            >
                {/* Back */}
                <Stack direction='row' justifyContent='start' width='100%'>
                    <IconButton aria-label="back" onClick={back}>
                        <ArrowBackIcon />
                    </IconButton>
                </Stack>

                {/* Title */}
                <Typography
                    fontWeight={700}
                    fontSize={{xs: 24, md: 40}}
                    letterSpacing={0.36}
                    mb={2}
                >
                    Verify mobile number
                </Typography>

                {/* Mobile number */}
                <Typography
                    fontWeight={400}
                    fontSize={{xs: 14, md: 20}}
                    letterSpacing={0.5}
                    color="rgba(128, 138, 147, 1)"
                    textAlign='center'
                >
                    {`Please enter the verification code sent to +94 *****${mobileNumber.substring(mobileNumber.length - 4)}`}
                </Typography>

                {/* Otp */}
                <OtpInput
                    value={otp}
                    onChange={setOtp}
                    numInputs={6}
                    renderSeparator={<div style={{paddingRight: '6px'}}></div>}
                    renderInput={(props) => <input {...props} />}
                    inputStyle={{
                        width: '45px',
                        height: '56px',
                        color: otpError ? 'red' : theme.palette.primary.main,
                        fontSize: '17px',
                        fontWeight: 'bolder',
                        backgroundColor: 'rgba(239, 239, 240, 1)',
                        border: otpError ? '1px solid red' : `1px solid rgba(239, 239, 240, 1)`,
                        borderRadius: '8px'
                    }}
                    containerStyle={{ paddingTop: '64px' }}
                    inputType="number"
                />

                {otpError ? (
                    <Typography
                        fontWeight={400}
                        fontSize={{xs: 14, md: 18}}
                        letterSpacing={0.2}
                        color="red"
                        textAlign='center'
                        mt={2}
                    >
                        {otpError}
                    </Typography>
                ) : <></>}

                {/* Resend */}
                <Stack direction='row' justifyContent='center' alignItems='center' mb={{xs: 2, md: 4}} mt={7}>
                    <Typography fontWeight={400} fontSize={{xs: 14, md: 18}} letterSpacing={0.2} color="rgba(34, 41, 46, 1)">
                        Didn't recieve the code?
                    </Typography>

                    {otpValid ? (
                        <Typography fontWeight={400} fontSize={{xs: 14, md: 18}} letterSpacing={0.2} color="rgba(34, 41, 46, 1)" ml={1}>
                            {otpValidTime < 10 ? `00:0${otpValidTime}` : `00:${otpValidTime}`}
                        </Typography>
                    ) : (
                        <ButtonComponent 
                            variant="text"
                            disabled={isLoadingRequestOtp}
                            label={
                                <Typography fontWeight={700} fontSize={{xs: 14, md: 20}} letterSpacing={0.2} color={theme.palette.primary.main}>
                                    Resend
                                </Typography>
                            }
                            sx={{ bgcolor: 'transparent' }}
                            disableRipple
                            onClick={requestOtpAgain}
                        />
                    )}
                </Stack>

                {/* Verify */}
                <ButtonComponent 
                    label='Verify'
                    inLoading={isLoadingVerifyUserOtp}
                    fullWidth
                    disabled={isLoadingRequestOtp}
                    onClick={verifyOtp}
                />
            </Stack>

            <SnackbarComponent 
                message={snackBar.message}
                color={snackBar.color}
                open={snackBar.open}
                handleClose={() => setSnackBar({ message: "", open: false })}
            />
        </>
    );
}

export default OtpView;
